import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPassword() {
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [isRecoverySession, setIsRecoverySession] = useState(false);
  const [success, setSuccess] = useState(false);

  const validationError = useMemo(() => {
    if (password.length < MIN_PASSWORD_LENGTH) return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    if (password !== confirmPassword) return "Passwords do not match.";
    return "";
  }, [confirmPassword, password]);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const queryParams = new URLSearchParams(window.location.search);
      const type = hashParams.get("type") ?? queryParams.get("type");
      const code = queryParams.get("code");

      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) throw error;
      }

      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      const hasRecoveryHint = type === "recovery";
      const hasAccessTokenInHash = Boolean(hashParams.get("access_token"));
      setIsRecoverySession(hasRecoveryHint || hasAccessTokenInHash);

      // Keep user signed in from magic-link session hydration, but do not allow
      // reset form unless this URL is explicitly a recovery flow.
      if (!hasRecoveryHint && !hasAccessTokenInHash && data.session) {
        setIsRecoverySession(false);
      }

      setSessionLoading(false);
    };

    run().catch(() => {
      if (!mounted) return;
      setIsRecoverySession(false);
      setSessionLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (!mounted) return;
      if (event === "PASSWORD_RECOVERY") {
        setIsRecoverySession(true);
      }
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setSuccess(true);
      toast.success("Password reset successful.");
      setTimeout(() => nav("/auth"), 1200);
    } catch (error: any) {
      toast.error(error?.message ?? "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container max-w-md py-20">
      <div className="te-card p-8">
        <h1 className="font-display text-3xl font-bold text-center">Reset your password</h1>
        <p className="text-muted-foreground text-center text-sm mt-1">
          Secure your AleaEdge account with a fresh password.
        </p>

        {sessionLoading ? (
          <p className="text-sm text-muted-foreground text-center mt-8">Verifying reset link…</p>
        ) : success ? (
          <div className="mt-8 space-y-4 text-center">
            <p className="text-sm text-muted-foreground">Your password has been updated successfully.</p>
            <Link to="/auth"><Button className="w-full bg-gradient-primary text-primary-foreground hover:opacity-90">Go to sign in</Button></Link>
          </div>
        ) : !isRecoverySession ? (
          <div className="mt-8 space-y-4 text-center">
            <p className="text-sm text-destructive">This password reset link is invalid or expired.</p>
            <Link to="/auth"><Button variant="secondary" className="w-full">Back to sign in</Button></Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-6">
            <div>
              <Label>New password</Label>
              <Input type="password" required minLength={MIN_PASSWORD_LENGTH} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div>
              <Label>Confirm password</Label>
              <Input type="password" required minLength={MIN_PASSWORD_LENGTH} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>
            {!!validationError && <p className="text-xs text-destructive">{validationError}</p>}
            <Button type="submit" disabled={loading || !!validationError} className="w-full bg-gradient-primary text-primary-foreground hover:opacity-90">
              {loading ? "Updating password…" : "Set new password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
